import { Chart } from '../Enums/Chart.enum';
import { Languages } from '../../../Enums/Common';
import moment, { Moment } from 'moment-timezone';

const formatLabelWithDate = (labelFormat: string) => (value: string) =>
	moment(value, 'x').format(labelFormat);

export const getDefaultOptionsForChartByName = (
	chartName: Chart,
	view: string,
	dateFormat: string,
	language: Languages,
) => {
	let labelFormat = dateFormat + ' HH:mm';

	switch (view) {
		case 'day':
			labelFormat = 'HH:mm';
			break;
		case 'week':
			labelFormat = 'DD.MM';
			break;
		case 'month':
			labelFormat = 'DD.MM';
			break;
		case 'year':
			labelFormat = 'MMM YYYY';
	}

	const formattingFn = formatLabelWithDate(labelFormat);

	const CHART_OPTIONS = {
		[Chart.BLOOD_PRESSURE]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 50,
						step: 10,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.BLOOD_PRESSURE_SYS_DIA]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 50,
						step: 10,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.WEIGHT]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 100,
						min: 50,
						step: 5,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.HEIGHT]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 100,
						min: 50,
						step: 5,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.WAISTLINE]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 100,
						min: 50,
						step: 5,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.BLOOD_GLUCOSE]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.STEPS]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 20000,
						min: 1000,
						step: 1000,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.BREATHING_RATE]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.OXYGEN_SATURATION]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.SLEEP]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.HEMOGLOBIN]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.HEMOGLOBIN_A1C]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.STRESS_LEVEL]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.ACTIVE_ENERGY]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.DISTANCE_WALKING_RUNNING]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.WORKOUT]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
		[Chart.ESAS]: {
			options: {
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
						max: 250,
						min: 0,
						step: 50,
					},
					x: {
						ticks: {
							callback: formattingFn,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'top',
					},
				},
				maintainAspectRatio: false,
			},
		},
	};

	return CHART_OPTIONS[chartName].options;
};
